import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, LogOut, UserCircle2, BadgeCheck, LayoutDashboard, ShieldCheck } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import ThemeToggle from "@/components/ThemeToggle";
import { VaultMark } from "@/components/Glyphs";
import { useAuth } from "@/context/AuthContext";
import { SITE, planLabel } from "@/data/site";

const NAV = [
  { label: "Features", href: "/#features" },
  { label: "Pricing", href: "/pricing" },
  { label: "About", href: "/about" },
  { label: "FAQ", href: "/faq" },
  { label: "Contact", href: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const { user, openAuth, logout } = useAuth();
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "border-b border-border bg-background/90 backdrop-blur-xl shadow-[0_8px_30px_-12px_rgba(0,0,0,0.6)]"
          : "border-b border-transparent bg-gradient-to-b from-background/90 via-background/55 to-transparent backdrop-blur-md"
      }`}
    >
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
        <a href="/" data-testid="navbar-logo" className="flex items-center gap-2.5 text-foreground drop-shadow-sm">
          <span className="text-gold"><VaultMark className="h-7 w-7" /></span>
          <span className="font-display text-lg font-extrabold tracking-tight text-foreground">Warranty_Vault pro</span>
        </a>

        <div className="hidden items-center gap-1 lg:flex">
          {NAV.map((item) => (
            <a
              key={item.href}
              href={item.href}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className="rounded-full px-3.5 py-2 text-sm font-semibold text-foreground/80 transition-colors hover:text-gold"
            >
              {item.label}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-2.5">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="flex items-center gap-2 rounded-full border border-border bg-card/60 py-1 pl-1 pr-3 transition-colors hover:border-gold/40">
                  <Avatar className="h-7 w-7">
                    <AvatarFallback className="bg-primary text-xs font-bold text-primary-foreground">
                      {(user.name || user.email || "U").slice(0, 1).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden max-w-[120px] truncate text-sm font-medium sm:inline">{user.name || user.email}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60" data-testid="user-menu">
                <DropdownMenuLabel className="flex flex-col">
                  <span className="truncate font-semibold">{user.name || "Your account"}</span>
                  <span className="truncate text-xs font-normal text-muted-foreground">{user.email}</span>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem disabled className="flex items-center gap-2 opacity-100">
                  <BadgeCheck className="h-4 w-4 text-gold" />
                  <span className="text-sm">Plan: <span className="font-semibold">{planLabel(user)}</span></span>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/account" data-testid="menu-account" className="flex items-center gap-2">
                    <LayoutDashboard className="h-4 w-4" /> My account
                  </Link>
                </DropdownMenuItem>
                {(user.is_super_root || user.is_super_admin || user.is_admin) && (
                  <DropdownMenuItem asChild>
                    <Link to="/console" data-testid="menu-console" className="flex items-center gap-2 text-gold focus:text-gold">
                      <ShieldCheck className="h-4 w-4" /> Owner console
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="logout-btn" onClick={logout} className="flex items-center gap-2 text-destructive focus:text-destructive">
                  <LogOut className="h-4 w-4" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <button
              onClick={() => openAuth("login")}
              data-testid="nav-login"
              className="hidden items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-transform hover:scale-[1.03] sm:inline-flex"
            >
              <UserCircle2 className="h-4 w-4" /> Log in
            </button>
          )}
          <ThemeToggle />
          <Sheet>
            <SheetTrigger asChild>
              <button
                aria-label="Open menu"
                data-testid="mobile-menu-trigger"
                className="grid h-9 w-9 place-items-center rounded-full border border-border bg-card/60 text-foreground/80 lg:hidden"
              >
                <Menu className="h-[18px] w-[18px]" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[82%] max-w-sm border-border bg-background" data-testid="mobile-menu">
              <div className="mt-8 flex flex-col gap-1">
                {NAV.map((item) => (
                  <SheetClose asChild key={item.href}>
                    <a
                      href={item.href}
                      data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                      className="rounded-xl px-4 py-3 text-base font-medium text-foreground/90 transition-colors hover:bg-accent hover:text-gold"
                    >
                      {item.label}
                    </a>
                  </SheetClose>
                ))}
                <SheetClose asChild>
                  {user ? (
                    <button
                      onClick={logout}
                      data-testid="mobile-logout"
                      className="mt-3 rounded-xl border border-border px-4 py-3 text-center text-base font-semibold text-destructive"
                    >
                      Log out
                    </button>
                  ) : (
                    <button
                      onClick={() => openAuth("login")}
                      data-testid="mobile-login"
                      className="mt-3 rounded-xl bg-primary px-4 py-3 text-center text-base font-semibold text-primary-foreground"
                    >
                      Log in / Sign up
                    </button>
                  )}
                </SheetClose>
                <SheetClose asChild>
                  <a
                    href={`mailto:${SITE.supportEmail}`}
                    className="mt-2 rounded-xl border border-border px-4 py-3 text-center text-base font-semibold text-foreground/90"
                  >
                    Email support
                  </a>
                </SheetClose>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
}
